/**
 * The footprint: volume at each price, split by who was the aggressor.
 *
 * ## Everything numeric here was computed on the server
 *
 * The columns, the rows, the point of control, the imbalance marks and the
 * shared price axis all arrive computed, or are derived by pure functions from
 * `@aegis/core`. This file places numbers and paints them. That is deliberate:
 * a footprint is a dense grid of small figures that a trader reads at a glance
 * and acts on, and the plausible failure is not a crash — it is a column that
 * renders beautifully and is wrong. Arithmetic in a renderer is arithmetic
 * nobody tests.
 *
 * ## Why the axis is shared and never per column
 *
 * Columns scaled to their own ranges look like a footprint and are a different
 * chart. Two adjacent columns would put different prices at the same height,
 * and "the volume moved up a level" — the read this chart exists for — becomes
 * unreadable. `footprintAxis` builds one ladder for the window; if the window
 * is too wide to draw, it says so rather than clipping, and this panel repeats
 * that rather than drawing part of a bar as though it were all of it.
 *
 * ## What is deliberately not here
 *
 * No trade-from-chart, no order entry. AEGIS never executes (`CLAUDE.md`), and
 * a price ladder is where that boundary is most tempting to blur.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type {
  FootprintBarDto, FootprintImbalanceDto, FootprintResponse,
} from '@aegis/api-client';
import { ApiError } from '@aegis/api-client';
import { busiestRow, dec, deltaTone, footprintAxis, type DeltaTone } from '@aegis/core';
import { api } from '../lib/api.js';

/**
 * Slower than the DOM's 1.5s.
 *
 * Only the bar in progress changes; the closed columns behind it are settled.
 * A footprint that redraws four times a second reads as a market moving when
 * it is a poll landing.
 */
const REFRESH_MS = 5000;

/** Timeframes the API will build a footprint on. Coarser has no readable grid. */
const TIMEFRAMES = ['1m', '3m', '5m', '15m', '30m', '1h'] as const;

/** How many columns fit on screen before the panel is just a scrollbar. */
const BAR_COUNT = 24;

export function Footprint({
  symbol, live,
}: {
  symbol: string;
  /** False during replay — see `OrderBook`. Same reason, same wording. */
  live: boolean;
}): JSX.Element {
  const [data, setData] = useState<FootprintResponse | null>(null);
  const [timeframe, setTimeframe] = useState<string>('1m');
  const [ratio, setRatio] = useState(3);
  const [refusal, setRefusal] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      setData(await api.footprint(symbol, timeframe, BAR_COUNT, { imbalanceRatio: ratio }));
      setRefusal(null);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 404)) {
        setData(null);
        setRefusal(e.message);
        // Stop polling a refusal. Retrying a licensing decision on a timer
        // achieves nothing and fills the log.
        if (timer.current) { clearInterval(timer.current); timer.current = null; }
      }
      // A transient failure is left alone — the next poll fixes it, and a
      // banner that flickers on a dropped request is worse than none.
    }
  }, [symbol, timeframe, ratio]);

  useEffect(() => {
    setRefusal(null);
    /* No symbol, no request. `GET /v1/footprint/` is a different route, and a
       404 from it is indistinguishable in the log from a symbol the feed does
       not carry — which is a condition worth noticing on its own. */
    if (!live || !symbol) { setData(null); return undefined; }
    void poll();
    timer.current = setInterval(() => { void poll(); }, REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [poll, live, symbol]);

  const view = useFootprintView(data);

  const head = (
    <div className="panel-head">
      <h3 className="panel-title">Footprint</h3>
      {data?.source === 'mock' && <span className="badge badge-warn">GENERATED</span>}
      {(data?.delayedByMs ?? 0) > 0 && (
        <span className="badge badge-warn">
          {Math.round((data?.delayedByMs ?? 0) / 60_000)}-MIN DELAYED
        </span>
      )}
    </div>
  );

  if (!symbol) {
    return (
      <aside className="panel footprint">
        {head}
        <p className="panel-empty">Pick an instrument to see its footprint.</p>
      </aside>
    );
  }

  if (!live) {
    return (
      <aside className="panel footprint">
        {head}
        <p className="panel-empty">
          Hidden during replay. The tape is live and the chart is not — showing
          them together is the confusion replay exists to prevent.
        </p>
      </aside>
    );
  }

  return (
    <aside className="panel footprint" aria-label="Footprint">
      {head}

      <div className="footprint-controls">
        <div className="chip-row" role="group" aria-label="Footprint timeframe">
          {TIMEFRAMES.map((t) => (
            <button
              key={t}
              className={`chip-btn compact ${timeframe === t ? 'on' : ''}`}
              onClick={() => setTimeframe(t)}
              aria-pressed={timeframe === t}
            >
              {t}
            </button>
          ))}
        </div>
        <label className="dim">
          imbalance
          <input
            type="number" min={1} max={20} step={1} value={ratio}
            onChange={(e) => setRatio(Math.min(20, Math.max(1, Number(e.target.value) || 3)))}
          />
          {'×'}
        </label>
      </div>

      {refusal && <div className="dom-refusal"><p>{refusal}</p></div>}

      {!refusal && data === null && <p className="panel-empty">Loading…</p>}

      {!refusal && data !== null && view === null && (
        <p className="panel-empty">
          {data.bars.length === 0
            ? 'No prints in this window yet.'
            : `These ${data.bars.length} bars span more price levels than a ladder `
              + `can show, even grouped at ${data.ticksPerRow} `
              + `${data.ticksPerRow === 1 ? 'tick' : 'ticks'} a row. A finer `
              + 'timeframe covers less range per column.'}
        </p>
      )}

      {view !== null && (
        <>
          <div className="footprint-grid" role="table" aria-label="Volume at price">
            <div className="footprint-axis" role="rowheader">
              <span className="footprint-time" aria-hidden="true" />
              {view.rows.map((price) => (
                <span key={price} className="footprint-price mono">{price}</span>
              ))}
              <span className="footprint-foot" aria-hidden="true" />
            </div>

            {view.columns.map((col) => (
              <div key={col.time} className="footprint-col" role="cell">
                <span className="footprint-time mono">{col.label}</span>
                {col.cells.map((cell) => (
                  <span
                    key={cell.price}
                    className={cellClass(cell)}
                    style={cell.heat > 0 ? { opacity: 0.25 + cell.heat * 0.75 } : undefined}
                    title={cellTitle(cell)}
                  >
                    {cell.traded ? (
                      <>
                        <b className="fp-bid mono">{cell.bid}</b>
                        <b className="fp-ask mono">{cell.ask}</b>
                      </>
                    ) : <i className="fp-blank" aria-hidden="true" />}
                  </span>
                ))}
                <span className={`footprint-foot mono tone-${col.tone}`}>
                  {col.deltaLabel}
                </span>
              </div>
            ))}
          </div>

          {data !== null && data.ticksPerRow > 1 && (
            <p className="field-hint">
              Each row is {data.ticksPerRow} ticks
              {data.groupingChosenByServer
                ? ` — one row per tick would be more than a ladder can show for ${data.symbol}.`
                : '.'}
            </p>
          )}

          <dl className="footprint-legend">
            <div><dt>×</dt><dd>bid volume × ask volume at that price</dd></div>
            <div><dt className="fp-key imb-buy" /><dd>
              buyers outpaced sellers one tick below by {ratio}× or more
            </dd></div>
            <div><dt className="fp-key imb-sell" /><dd>
              sellers outpaced buyers one tick above by {ratio}× or more
            </dd></div>
            <div><dt className="fp-key poc" /><dd>the price that traded most in that bar</dd></div>
          </dl>

          <p className="field-hint">
            An imbalance compares the volume that lifted the offer at a price
            against the volume that hit the bid one tick below it — the two
            orders that actually competed. Comparing the two sides at the same
            price compares a buyer and a seller who were never in the same queue.
          </p>

          {view.anyUnreadable && (
            <p className="field-hint">
              Delta is left blank on bars where more than{' '}
              {Math.round((data?.unreadableAbove ?? 1 / 3) * 100)}% of the volume
              arrived without a side. That figure would be computed from the
              minority that could be classified, which is not a reading of the bar.
            </p>
          )}
        </>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------

interface Cell {
  readonly price: string;
  readonly bid: number;
  readonly ask: number;
  readonly traded: boolean;
  /** 0–1 against the busiest row in THIS column, never across the window. */
  readonly heat: number;
  readonly poc: boolean;
  readonly imbalance: 'buy' | 'sell' | null;
  /** What the mark says on hover. Null ratio reads as words, never a number. */
  readonly imbalanceLabel: string | null;
}

interface Column {
  readonly time: number;
  readonly label: string;
  readonly cells: readonly Cell[];
  /** Describes what the numbers say — never what to do about it. */
  readonly tone: DeltaTone;
  readonly deltaLabel: string;
}

interface View {
  readonly rows: readonly string[];
  readonly columns: readonly Column[];
  readonly anyUnreadable: boolean;
}

/**
 * The grid, or null when there is nothing drawable.
 *
 * Null covers both "no prints" and "too wide to draw", because the panel says
 * something different about each and can tell them apart from `data.bars`
 * without a second return shape.
 */
function useFootprintView(data: FootprintResponse | null): View | null {
  return useMemo(() => {
    if (data === null || data.bars.length === 0) return null;

    const axis = footprintAxisOf(data);
    if (axis === null) return null;

    return {
      rows: axis,
      anyUnreadable: data.bars.some((b) => b.unclassifiedShare > data.unreadableAbove),
      columns: data.bars.map((bar) => toColumn(bar, axis)),
    };
  }, [data]);
}

/**
 * The shared ladder.
 *
 * `footprintAxis` from `@aegis/core`, not a walk over the prices here. The DTO
 * is structurally the core type, and the alternative is the same tick
 * arithmetic written twice — once tested, once in a render pass where it
 * cannot be. It is also what keeps this panel and the mobile one agreeing
 * about where a row sits.
 *
 * Null covers "nothing traded", "the window is wider than a ladder can show",
 * and "the tick size is broken". The panel can tell them apart from
 * `data.bars`, so a second return shape would earn nothing.
 */
function footprintAxisOf(data: FootprintResponse): readonly string[] | null {
  // `rowSize`, not `tickSize`. A footprint grouped four ticks to a row has
  // rows four ticks apart, and an axis built on the tick would draw three
  // empty rows between every pair that traded.
  return footprintAxis(data.bars, dec(data.rowSize), data.precision, MAX_ROWS)?.rows ?? null;
}

/** Beyond this the panel is a wall of one-pixel rows, and the browser suffers. */
const MAX_ROWS = 400;

function toColumn(bar: FootprintBarDto, axis: readonly string[]): Column {
  const byPrice = new Map(bar.levels.map((l) => [l.price, l]));
  const marks = new Map(bar.imbalances.map((i) => [i.price, i]));
  // Per column, never across the window: shading answers "where in THIS bar
  // did the volume sit", and a window scaled to its busiest bar leaves every
  // quiet bar uniformly blank.
  const peak = busiestRow(bar);
  const tone = deltaTone(bar);

  return {
    time: bar.time,
    label: new Date(bar.time * 1000).toLocaleTimeString(undefined, {
      hour12: false, hour: '2-digit', minute: '2-digit',
    }),
    tone,
    // Blank, not zero. Zero means the two sides matched, which is a different
    // statement from "most of this bar arrived without a side".
    deltaLabel: tone === 'unreadable' ? '—' : `${bar.delta > 0 ? '+' : ''}${bar.delta}`,
    cells: axis.map((price) => {
      const level = byPrice.get(price);
      return {
        price,
        bid: level?.bidVolume ?? 0,
        ask: level?.askVolume ?? 0,
        traded: level !== undefined,
        heat: level && peak > 0 ? level.total / peak : 0,
        poc: bar.pointOfControl === price,
        imbalance: marks.get(price)?.side ?? null,
        imbalanceLabel: imbalanceLabel(marks.get(price)),
      };
    }),
  };
}

/**
 * What an imbalance mark says.
 *
 * A null ratio is not a missing number — it means nothing traded against that
 * price at all, which is the strongest reading of the mark. It is spelled out
 * in words rather than shown as a large multiple, because a printed figure is
 * taken as measured and nothing was measured.
 */
function imbalanceLabel(mark: FootprintImbalanceDto | undefined): string | null {
  if (!mark) return null;
  const who = mark.side === 'buy' ? 'buyers' : 'sellers';
  return mark.ratio === null
    ? `${who} unopposed one tick ${mark.side === 'buy' ? 'below' : 'above'}`
    : `${who} ahead ${mark.ratio}× on the diagonal`;
}

function cellTitle(cell: Cell): string | undefined {
  if (!cell.traded) return undefined;
  const base = `${cell.price}: ${cell.bid} × ${cell.ask}`;
  return cell.imbalanceLabel ? `${base} — ${cell.imbalanceLabel}` : base;
}

function cellClass(cell: Cell): string {
  const parts = ['footprint-cell'];
  if (!cell.traded) parts.push('empty');
  if (cell.poc) parts.push('poc');
  if (cell.imbalance) parts.push(`imb-${cell.imbalance}`);
  return parts.join(' ');
}
